import { create } from "zustand"

import "../graphql/queries"
import type { ApAgingTotals, ApAgingVendor } from "../types/apAgingReport"
import type { ApAgingReportSearchParams } from "../types/searchParams"
import { toApAgingReportInput, toApAgingTotals, toApAgingVendors } from "../utils"

import {
  type ApAgingReportInput,
  GetApAgingReportDocument,
  type GetApAgingReportQuery,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type ApAgingSearchFilters = SearchFilters &
  Pick<ApAgingReportSearchParams, "statuses" | "agingBuckets" | "dueDateFrom" | "dueDateTo">

const deduplicateVendors = (newVendors: ApAgingVendor[], existing: ApAgingVendor[]): ApAgingVendor[] => {
  const seen = new Set(existing.map((vendor) => vendor.vendorCompanyId))
  return [...existing, ...newVendors.filter((vendor) => !seen.has(vendor.vendorCompanyId))]
}

interface ExtractedApAgingReportResponse {
  __typename: GetApAgingReportQuery["financialReporting"]["apAgingReport"]["__typename"]
  data: ApAgingVendor[]
  // The totals over the whole filtered set, which come with every page.
  meta?: ApAgingTotals
  error?: { message: string }
}

export const extractApAgingReportResponse = (data: GetApAgingReportQuery): ExtractedApAgingReportResponse => {
  const result = data.financialReporting.apAgingReport

  if (result.__typename === "ApAgingReportSuccess") {
    return {
      __typename: result.__typename,
      data: toApAgingVendors(result.report.vendors),
      meta: toApAgingTotals(result.report.summary),
    }
  }

  if (result.__typename === "ApAgingReportFailure") {
    return { __typename: result.__typename, data: [], error: result.error }
  }

  // createSearchStore only rethrows when __typename ends in "Failure", so an unknown shape has to throw here.
  throw new Error(`Unexpected response type: ${(result as { __typename: string }).__typename}`)
}

export const useApAgingSearchStore = create(
  createSearchStore<
    ApAgingVendor,
    ApAgingSearchFilters,
    GetApAgingReportQuery,
    ApAgingReportInput,
    { input: ApAgingReportInput },
    ApAgingTotals
  >({
    documentNode: GetApAgingReportDocument,
    deduplicateData: deduplicateVendors,

    getQueryInput: (filters, offset, limit) => toApAgingReportInput(filters, limit, offset),

    getQueryVariables: (queryInput) => ({ input: queryInput }),

    extractDataFromResponse: extractApAgingReportResponse,
  })
)
